import { directSupabase } from '../../lib/supabaseClient';

type KeyDateEventRow = {
  id: string;
  event_date: string | null;
  status: string | null;
  metadata?: { manualComplete?: boolean } | null;
  created_at?: string | null;
};

function isDated(event: KeyDateEventRow | undefined) {
  if (!event?.event_date) return false;
  const date = new Date(event.event_date);
  return !Number.isNaN(date.getTime());
}

function isManualComplete(event: KeyDateEventRow | undefined) {
  if (!event || isDated(event)) return false;
  if (event.status === 'completed') return true;
  return !!event.metadata?.manualComplete;
}

async function latestKeyDateEvent(projectId: string, eventType: string): Promise<KeyDateEventRow | undefined> {
  const { data } = await directSupabase
    .from('project_events')
    .select('id, event_date, status, metadata, created_at')
    .eq('project_id', projectId)
    .eq('event_type', eventType)
    .order('created_at', { ascending: false });
  const rows = ((data || []) as KeyDateEventRow[]).filter((row) => row.status !== 'cancelled');
  return rows.sort((a, b) => {
    const aDated = isDated(a) ? 1 : 0;
    const bDated = isDated(b) ? 1 : 0;
    if (aDated !== bDated) return bDated - aDated;
    return (Date.parse(b.event_date || b.created_at || '') || 0) - (Date.parse(a.event_date || a.created_at || '') || 0);
  })[0];
}

export async function setKeyDateManualComplete(
  projectId: string,
  eventType: string,
  completed: boolean
): Promise<void> {
  const existing = await latestKeyDateEvent(projectId, eventType);
  if (isDated(existing)) return;

  if (completed) {
    if (isManualComplete(existing) && existing) return;
    if (existing) {
      const { error } = await directSupabase
        .from('project_events')
        .update({
          status: 'completed',
          metadata: { ...(existing.metadata || {}), manualComplete: true },
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id);
      if (error) throw new Error(error.message);
      return;
    }
    const { error } = await directSupabase.from('project_events').insert({
      project_id: projectId,
      event_type: eventType,
      event_date: null,
      calendar_event_id: null,
      status: 'completed',
      metadata: { manualComplete: true },
      notes: 'Marked complete without Outlook booking',
    });
    if (error) throw new Error(error.message);
    return;
  }

  if (existing && isManualComplete(existing)) {
    const { error } = await directSupabase.from('project_events').delete().eq('id', existing.id);
    if (error) throw new Error(error.message);
  }
}
